'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { twConfig } from '@/lib/tailwind/twConfig'
import { assertNever } from '@/lib/typescript/assertNever'
import { type Image, type ImageUrl, Media } from '@gql-generated/graphql'
import { type VariantProps, cva } from 'class-variance-authority'

import { gql } from '@gql-generated/gql'

export const PICTURE_PLACEHOLDER_IMAGE_URL = '/placeholder-image.svg'

gql(/* GraphQL */ `
  fragment FullPicture on Image {
    alt
    set {
      url
      media
    }
  }
`)

// #region PlaceholderPicture

export type PlaceholderPictureProps = PictureVariantProps &
  UiComponentProps<{
    alt?: string | null
    width?: string
    height?: string
  }>

export function PlaceholderPicture({
  alt,
  className,
  width,
  height,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: PlaceholderPictureProps): React.ReactElement {
  const { t } = useTranslation(['picture'])

  return (
    <img
      id={id}
      className={twMerge(className, 'object-contain')}
      src={PICTURE_PLACEHOLDER_IMAGE_URL}
      alt={alt || t('picture:placeholderAlt')}
      aria-label={ariaLabel}
      width={width}
      height={height}
      data-testid={dataTestId ?? 'picture/placeholder'}
    />
  )
}

// #endregion

// #region Picture

export const imageModes = {
  cover: 'object-cover',
  contain: 'object-contain',
}

const pictureCva = cva('', {
  variants: {
    mode: imageModes,
  },
})

export type PictureVariantProps = VariantProps<typeof pictureCva>

export type PictureProps = PictureVariantProps &
  WithPayloadBlockId &
  UiComponentProps<{
    image: Image
    shrink?: boolean
    pictureClassName?: string
    // TODO: make width and height required
    width?: string
    height?: string
    isDecorative?: boolean
    onError?: () => void
  }>

export function Picture({
  image,
  className,
  pictureClassName,
  mode = 'cover',
  shrink = false,
  width,
  height,
  isDecorative = false,
  onError,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'data-payload-block-id': dataPayloadBlockId,
}: PictureProps): React.ReactElement {
  const { screens } = twConfig.theme
  const { alt, set } = image

  const baseImage = set ? set.find((set: ImageUrl) => set.media === Media.Mobile) || set[0] : null

  if (!baseImage) {
    return (
      <PlaceholderPicture
        alt={alt}
        className={className}
        id={id}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      />
    )
  }

  return (
    <picture
      id={id}
      className={twMerge(pictureClassName, shrink ? 'shrink' : 'shrink-0')}
      data-testid={dataTestId ?? 'picture'}
    >
      {set?.map((source) => {
        let mediaQuery = ''

        if (source.media === Media.Mobile) {
          mediaQuery = `(max-width: ${Number.parseInt(screens.desktop, 10) - 1}px)`
        } else if (source.media === Media.Desktop) {
          mediaQuery = `(min-width: ${screens.desktop})`
        } else {
          assertNever(source.media)
        }

        return (
          <source
            key={`${source.media}-${source.url}`}
            media={mediaQuery}
            srcSet={source.url}
            type="image/jpg"
            data-testid={`picture/source/${source.media}`}
          />
        )
      })}

      <img
        alt={alt || undefined}
        aria-label={ariaLabel}
        src={baseImage.url}
        data-testid="picture/image"
        className={twMerge(pictureCva({ mode }), className)}
        width={width || undefined}
        height={height || undefined}
        role={isDecorative ? 'none' : 'img'}
        loading="lazy"
        onError={onError}
        data-payload-block-id={dataPayloadBlockId}
      />
    </picture>
  )
}

// #endregion
